import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import RestaurantCard from './RestaurantCard';
import StoreCardSkeleton from './StoreCardSkeleton';
import PremiumModal from './PremiumModal';
import { StaggeredContainer, StaggeredItem, GlassCard } from './AnimatedPage';
import { Filter, SlidersHorizontal, Heart } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { stores } from '@/data/restaurants';
import { useFavorites } from '@/hooks/useFavorites';

import africanFood from '@/assets/african-food.webp';
import asianFood from '@/assets/asian-food.jpg';
import pizzaFood from '@/assets/pizza-food.jpg';
import bunnyChow from '@/assets/bunny-chow.jpg';
import kota from '@/assets/kota.jpg';
import gatsby from '@/assets/gatsby.jpg';
import vetkoek from '@/assets/vetkoek.jpg';
import papVleis from '@/assets/pap-vleis.jpg';
import boereworsRoll from '@/assets/boerewors-roll.jpg';
import engenLogo from '@/assets/engen-logo.jpg';
import astronLogo from '@/assets/astron-logo.jpg';
import shellLogo from '@/assets/shell-logo.jpg';

const mockStores = [
  ...stores.map(store => {
    // Map image string references to actual imports
    let imageUrl = store.image;
    if (store.image === 'bunny-chow') imageUrl = bunnyChow;
    else if (store.image === 'kota') imageUrl = kota;
    else if (store.image === 'gatsby') imageUrl = gatsby;
    else if (store.image === 'vetkoek') imageUrl = vetkoek;
    else if (store.image === 'pap-vleis') imageUrl = papVleis;
    else if (store.image === 'boerewors-roll') imageUrl = boereworsRoll;
    else if (store.image === 'engen-logo') imageUrl = engenLogo;
    else if (store.image === 'astron-logo') imageUrl = astronLogo;
    else if (store.image === 'shell-logo') imageUrl = shellLogo;
    
    return {
      id: store.id,
      name: store.name,
      cuisine: store.cuisine,
      rating: store.rating,
      reviewCount: store.reviewCount,
      deliveryTime: store.deliveryTime,
      deliveryFee: store.deliveryFee,
      image: imageUrl,
      isOpen: store.isOpen,
      badges: store.deliveryFee === 0 ? ['Free Delivery'] : [],
      distance: '2.1 km'
    };
  }),
  {
    id: 'mama-africa',
    name: 'Mama Africa Kitchen',
    cuisine: 'Traditional African',
    rating: 4.8,
    reviewCount: 324,
    deliveryTime: '25-40 min',
    deliveryFee: 0,
    image: africanFood,
    isOpen: true,
    badges: ['Free Delivery', 'Most Popular'],
    distance: '1.2 km',
    promo: '20% OFF'
  }
];

const StoresGrid = () => {
  const { t } = useLanguage();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [displayCount, setDisplayCount] = useState(20);
  const [activeFilters, setActiveFilters] = useState<string[]>(['All']);
  const [sortBy, setSortBy] = useState<'shuffle' | 'rating' | 'deliveryTime' | 'deliveryFee'>('shuffle');
  const shuffledStores = useMemo(() => {
    const arr = [...mockStores];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, []);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const toggleFilter = (filter: string) => {
    if (filter === 'All') {
      setActiveFilters(['All']);
    } else {
      const newFilters = activeFilters.includes(filter)
        ? activeFilters.filter(f => f !== filter)
        : [...activeFilters.filter(f => f !== 'All'), filter];
      setActiveFilters(newFilters.length === 0 ? ['All'] : newFilters);
    }
  };

  const filteredAndSortedStores = useMemo(() => {
    let results = sortBy === 'shuffle' ? [...shuffledStores] : [...mockStores];

    // Apply filters
    if (!activeFilters.includes('All')) {
      results = results.filter(store => {
        return activeFilters.every(filter => {
          if (filter === 'Free Delivery') return store.deliveryFee === 0;
          if (filter === 'Under 30 min') return parseInt(store.deliveryTime.split('-')[0]) < 30;
          if (filter === 'Highly Rated') return store.rating >= 4.5;
          if (filter === 'Promotions') return 'promo' in store;
          if (filter === 'Open Now') return store.isOpen;
          return true;
        });
      });
    }

    // Sort results
    if (sortBy !== 'shuffle') {
      results.sort((a, b) => {
        if (sortBy === 'rating') {
          return b.rating - a.rating;
        } else if (sortBy === 'deliveryTime') {
          const aTime = parseInt(a.deliveryTime.split('-')[0]);
          const bTime = parseInt(b.deliveryTime.split('-')[0]);
          return aTime - bTime;
        } else if (sortBy === 'deliveryFee') {
          return a.deliveryFee - b.deliveryFee;
        }
        return 0;
      });
    }

    return results;
  }, [activeFilters, sortBy, shuffledStores]);

  const visibleStores = filteredAndSortedStores.slice(0, displayCount);
  const hasMore = displayCount < filteredAndSortedStores.length;

  return (
    <section id="stores-section" className="py-16 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 sm:mb-8 gap-3 sm:gap-4">
          <div className="text-center lg:text-left px-4 lg:px-0">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">
              {t('stores.title')}
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
              {t('stores.subtitle')}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center lg:justify-end gap-2 sm:gap-3 px-4 lg:px-0">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="btn-ghost-neon flex-1 sm:flex-none text-xs sm:text-sm h-9 sm:h-10"
            >
              <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Filters
            </Button>
            
            <div className="flex gap-2">
              <Button 
                variant={sortBy === 'rating' ? 'default' : 'outline'} 
                size="sm"
                className="flex-1 sm:flex-none text-xs sm:text-sm h-9 sm:h-10"
                onClick={() => setSortBy('rating')}
              >
                Rating
              </Button>
              <Button 
                variant={sortBy === 'deliveryTime' ? 'default' : 'outline'} 
                size="sm"
                className="flex-1 sm:flex-none text-xs sm:text-sm h-9 sm:h-10"
                onClick={() => setSortBy('deliveryTime')}
              >
                Time
              </Button>
              <Button 
                variant={sortBy === 'deliveryFee' ? 'default' : 'outline'} 
                size="sm"
                className="flex-1 sm:flex-none text-xs sm:text-sm h-9 sm:h-10"
                onClick={() => setSortBy('deliveryFee')}
              >
                Fee
              </Button>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        {showFilters && (
          <div className="mb-8 p-4 bg-dark-surface rounded-xl border border-border/40">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {['All', 'Free Delivery', 'Under 30 min', 'Highly Rated', 'Promotions', 'Open Now'].map(filter => (
                <Button 
                  key={filter}
                  size="sm" 
                  className={activeFilters.includes(filter) ? "category-pill category-pill-active" : "category-pill"}
                  onClick={() => toggleFilter(filter)}
                >
                  {filter}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
          <p className="text-sm text-muted-foreground">
            Showing {visibleStores.length} of {filteredAndSortedStores.length} stores
          </p>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="flex rounded-lg border border-border/40 overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1 text-xs ${
                  viewMode === 'grid' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 text-xs ${
                  viewMode === 'list' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                List
              </button>
            </div>
          </div>
        </div>

        {/* Stores Grid/List */}
        {isLoading ? (
          <div className={`
            ${viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6' 
              : 'flex flex-col gap-3 sm:gap-4'
            }
          `}>
            {Array.from({ length: 8 }).map((_, i) => (
              <StoreCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <StaggeredContainer className={`
            ${viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6' 
              : 'flex flex-col gap-3 sm:gap-4'
            }
          `}>
            {visibleStores.map((store) => (
              <StaggeredItem key={store.id}>
                <Link to={`/store/${store.id}`}>
                  {viewMode === 'grid' ? (
                    <RestaurantCard {...store} />
                  ) : (
                    <GlassCard className="overflow-hidden">
                      <div className="flex flex-col sm:flex-row gap-4 p-4">
                        {/* Image Section */}
                        <div className="relative w-full sm:w-48 h-48 sm:h-32 flex-shrink-0">
                          <img 
                            src={store.image} 
                            alt={store.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          {/* Favorite Heart Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleFavorite(store.id);
                            }}
                            className="absolute top-2 right-2 z-10 p-2 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background transition-all duration-200 hover:scale-110"
                            aria-label={isFavorite(store.id) ? "Remove from favorites" : "Add to favorites"}
                          >
                            <Heart 
                              className={`h-4 w-4 transition-colors ${
                                isFavorite(store.id) 
                                  ? 'fill-primary text-primary' 
                                  : 'text-muted-foreground hover:text-primary'
                              }`}
                            />
                          </button>
                        </div>
                        
                        {/* Content Section */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg sm:text-xl font-bold truncate">{store.name}</h3>
                              <p className="text-sm text-muted-foreground">{store.cuisine}</p>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-1">
                                <span className="text-yellow-400">★</span>
                                <span className="font-semibold">{store.rating}</span>
                                <span className="text-muted-foreground">({store.reviewCount})</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Badges */}
                          {store.badges && store.badges.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {store.badges.map((badge, idx) => (
                                <span key={idx} className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full">
                                  {badge}
                                </span>
                              ))}
                              {'promo' in store && (
                                <span className="text-xs px-2 py-0.5 bg-yellow-400/20 text-yellow-400 rounded-full font-semibold">
                                  {(store as any).promo}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Meta Info */}
                          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground mt-auto">
                            <span>🕒 {store.deliveryTime}</span>
                            <span>
                              {store.deliveryFee === 0 ? (
                                <span className="text-primary font-semibold">Free Delivery</span>
                              ) : (
                                `R${store.deliveryFee} delivery`
                              )}
                            </span>
                            <span>📍 {store.distance}</span>
                          </div>
                        </div>
                      </div>
                    </GlassCard>
                  )}
                </Link>
              </StaggeredItem>
            ))}
          </StaggeredContainer>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="text-center mt-8 sm:mt-10 md:mt-12 px-4">
            <Button 
              onClick={() => setDisplayCount(prev => prev + 20)}
              className="btn-neon px-6 sm:px-8 w-full sm:w-auto text-sm sm:text-base h-10 sm:h-11 md:h-auto"
            >
              {t('stores.loadMore')}
            </Button>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2 sm:mt-3">
              Showing {visibleStores.length} of {filteredAndSortedStores.length} stores
            </p>
          </div>
        )}

        {/* Promotional Banner */}
        <div className="mt-8 sm:mt-12 md:mt-14 lg:mt-16 p-4 sm:p-6 md:p-8 lg:p-10 bg-gradient-neon rounded-lg sm:rounded-xl md:rounded-2xl text-center relative overflow-hidden mx-0 sm:mx-2 lg:mx-0">
          <div className="relative z-10 max-w-3xl mx-auto">
            <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-midnight mb-2 sm:mb-2 md:mb-3 px-2">
              Hungry for more savings?
            </h3>
            <p className="text-xs sm:text-sm md:text-base text-midnight/80 mb-3 sm:mb-4 md:mb-6 max-w-xs sm:max-w-md mx-auto px-2 leading-relaxed">
              Get 25% off your next 3 orders with LUMZA Premium
            </p>
            <Button 
              onClick={() => setShowPremiumModal(true)}
              className="bg-midnight text-moonlight hover:bg-midnight/90 w-full sm:w-auto px-4 sm:px-6 md:px-8 text-xs sm:text-sm md:text-base h-10 sm:h-11 font-semibold touch-target"
            >
              Join LUMZA Premium
            </Button>
          </div>
          
          {/* Background Effects */}
          <div className="absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-12 h-12 sm:w-16 sm:h-16 md:w-24 md:h-24 bg-white/5 rounded-full blur-xl"></div>
        </div>
      </div>

      {/* Premium Modal */}
      <PremiumModal 
        open={showPremiumModal} 
        onOpenChange={setShowPremiumModal} 
      />
    </section>
  );
};

export default StoresGrid;